/**
 * Widget V2 — Locations index (discovery)
 *
 * Browse the club's locations. Cards link to a detail page with address,
 * hours, contact and CTAs into booking scoped to that location.
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import CardActionArea from "@mui/material/CardActionArea";
import { alpha } from "@mui/material/styles";
import LocationOnIcon from "@mui/icons-material/LocationOn";

import { useWidget } from "/imports/state/WidgetContext";
import { EmptyState, useImageFallback } from "/imports/ui/components/shared";
import { PageHeading, DISCOVERY_CARD_SX } from "/imports/ui/components/discovery";

/** Image band — renders the location's photo (`imgUrls[0]`) when available,
    otherwise a branded gradient placeholder. */
export function LocationBanner({ height = 120, src }) {
  const { showImage, onImageError } = useImageFallback(src);

  if (showImage) {
    return <Box component="img" src={src} alt="" onError={onImageError} sx={{ display: "block", width: "100%", height, objectFit: "cover" }} />;
  }
  return (
    <Box
      sx={{
        height,
        background: (theme) =>
          `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.primary.main, 0.06)} 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      <LocationOnIcon sx={{ color: (theme) => alpha(theme.palette.primary.main, 0.55), fontSize: height * 0.4 }} />
    </Box>
  );
}

function LocationsView() {
  const navigate = useNavigate();
  const { slug, locations } = useWidget();

  return (
    <Box>
      <PageHeading title="Our Locations" subtitle="Find a venue near you — addresses, hours and contact." onBack={() => navigate(`/${slug}`)} />

      {(locations || []).length === 0 ? (
        <EmptyState title="No locations listed" description="There are no locations to show yet." />
      ) : (
        <Grid container spacing={2}>
          {locations.map((loc) => (
            <Grid item xs={12} sm={6} md={4} key={loc._id}>
              <Box sx={{ ...DISCOVERY_CARD_SX, overflow: "hidden", height: "100%" }}>
                <CardActionArea onClick={() => navigate(`/${slug}/locations/${loc._id}`)} sx={{ height: "100%" }}>
                  <LocationBanner src={loc.imgUrls?.[0]} />
                  <Box sx={{ p: 2 }}>
                    <Typography fontWeight={700} noWrap>
                      {loc.name}
                    </Typography>
                    {loc.location && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.25, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                      >
                        {loc.location}
                      </Typography>
                    )}
                  </Box>
                </CardActionArea>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

export default LocationsView;
